import * as yup from 'yup';
import {
  lettersOrNumbersWithExtraChars,
  lettersOrNumbersNoSpecialChars,
  lettersOrNumbersNoSpecialCharsMessage,
  onlyNumbers,
  onlyNumbersMessage,
  textTooLong,
  requiredMessage,
  endBeforeStart,
  injuryDate,
  bornDateAndInjury,
  phoneNumberMessage,
  lettersOrNumbersWithExtraCharsMessage,
} from 'Regex';
import { isBefore } from 'date-fns';
import { ICaseDetailsInputs } from 'API';
import { store } from 'Store';
import { CASE_TYPE_RELATION_ID } from 'Enum';
import { ON_THE_WAY_TO_BASE_OR_FROM_BASE } from '../SecondSection/InjuryTraumaQuestionnaire/WhenInjuryHappend';
import { WAS_DRIVER_DURING_ACCIDENT_ID } from '../ThirdSection/WhatDidDoDuringAccident';
import { TRAFFIC_ACCIDENT_NO_INSURANCE_SUE_ID } from '../ThirdSection/Insurance';
import {
  KNOWN_WITNESSES_ID,
  UNKNOWN_WITNESSES_ID,
} from '../FourthSection/Witnesses';
import { CAREER_MILITARY_SERVICE } from '../SecondSection/DiseaseMentalQuestionnaire/DiagnosisExist';

const extractIndexFromPath = (path: string) => {
  const charIndex = path.indexOf('[');
  return path.substring(charIndex + 1, charIndex + 2);
};

export const caseDetailsSchema: yup.SchemaOf<ICaseDetailsInputs> = yup
  .object()
  .shape({
    caseDetails: yup
      .array()
      .of(
        yup.object({
          isCausedBySpecificEvent: yup
            .boolean()
            .when('caseTypeSection', {
              is: (val: any) => val.isInjury || val.isPostTrauma,
              then: yup.boolean().required(requiredMessage).nullable(),
              otherwise: yup.boolean().notRequired().nullable(),
            })
            .nullable(),
          isClassifiedDocsApproved: yup
            .boolean()
            .oneOf([true], requiredMessage)
            .nullable(),
          areOtherRelevantFiles: yup
            .boolean()
            .required(requiredMessage)
            .nullable(),
          hasNoHmoFile: yup.boolean().notRequired().nullable(),
          isPrevHmoExist: yup.boolean().required(requiredMessage).nullable(),
          hasNoPrevHmoFile: yup.boolean().notRequired().nullable(),
          areThereMedicalDocuments: yup
            .boolean()
            .required(requiredMessage)
            .nullable(),
          areThereWitnessesOptionId: yup
            .number()
            .required(requiredMessage)
            .nullable(),
          caseTypeRelationId: yup.string().notRequired().nullable(),
          diseaseKindSection: yup
            .object({
              diseaseOptionId: yup
                .string()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context: any) => {
                    const index = extractIndexFromPath(context.path);
                    const formData = context.from[2].value.caseDetails[index];

                    if (
                      value &&
                      formData.diseaseKindSection.isDiagnosisExist &&
                      formData.caseTypeSection.isDisease &&
                      formData.militaryServiceSection.serviceTypeId ===
                        CAREER_MILITARY_SERVICE
                    )
                      return true;

                    if (
                      formData.militaryServiceSection.serviceTypeId !==
                        CAREER_MILITARY_SERVICE ||
                      !formData.caseTypeSection.isDisease ||
                      !formData.diseaseKindSection.isDiagnosisExist
                    )
                      return true;

                    return false;
                  },
                })
                .nullable(),
              isDiseaseDescribedInFile: yup
                .boolean()
                .when('isDiagnosisExist', {
                  is: (val: boolean) => val === true,
                  then: yup.boolean().required(requiredMessage).nullable(),
                  otherwise: yup.boolean().notRequired().nullable(),
                })
                .nullable(),
              relativeRelation: yup
                .string()
                .when('isRelativesWithSameDisease', {
                  is: (val: any) => val === true,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .nullable(),
              isDiagnosisExist: yup
                .boolean()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj.caseType[
                        index
                      ];

                    if (
                      currentCaseType.type ===
                        CASE_TYPE_RELATION_ID.MENTAL_ID ||
                      currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                    ) {
                      if (value === null || value === undefined) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              diseaseDetailsDescription: yup
                .string()
                .when('isDiseaseDescribedInFile', {
                  is: (val: any) => val === false,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(500, textTooLong(500))
                .matches(
                  lettersOrNumbersWithExtraChars,
                  lettersOrNumbersWithExtraCharsMessage
                )
                .nullable(),
              diseaseDiagnosisDate: yup
                .string()
                .when('isDiagnosisExist', {
                  is: (val: boolean) => val === true,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .nullable(),
              isRelativesWithSameDisease: yup
                .boolean()
                .when('isDiagnosisExist', {
                  is: (val: boolean) => val === true,
                  then: yup.boolean().required(requiredMessage).nullable(),
                  otherwise: yup.boolean().notRequired().nullable(),
                })
                .nullable(),
              relativesWithSameDisease: yup
                .string()
                .when('isRelativesWithSameDisease', {
                  is: (val: any) => val === true,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(500, textTooLong(500))
                .matches(
                  lettersOrNumbersWithExtraChars,
                  lettersOrNumbersWithExtraCharsMessage
                )
                .nullable(),
            })
            .notRequired()
            .nullable(),
          hospitalizationDetailsSection: yup
            .array()
            .when('wasMedicalInstitutionTreatment', {
              is: (val: any) => val === true,
              then: yup.array().of(
                yup.object({
                  wasHospitalized: yup
                    .boolean()
                    .required(requiredMessage)
                    .nullable(),
                  hasNoHospitalizationStartDate: yup
                    .boolean()
                    .notRequired()
                    .nullable(),
                  hasNoHospitalizationEndDate: yup
                    .boolean()
                    .notRequired()
                    .nullable(),
                  hasNoClinicDepartment: yup.boolean().notRequired().nullable(),
                  hasNoDoctorName: yup.boolean().notRequired().nullable(),
                  description: yup
                    .string()
                    .when('wasHospitalized', {
                      is: (val: boolean) => val === true,
                      then: yup.string().required(requiredMessage).nullable(),
                      otherwise: yup.string().notRequired().nullable(),
                    })
                    .max(500, textTooLong(500)),
                  clinicDepartment: yup
                    .string()
                    .when(['hasNoClinicDepartment', 'wasHospitalized'], {
                      is: (
                        hasNoClinicDepartment: any,
                        wasHospitalized: boolean
                      ) =>
                        hasNoClinicDepartment === true ||
                        wasHospitalized !== true,
                      then: yup.string().notRequired().nullable(),
                      otherwise: yup
                        .string()
                        .required(requiredMessage)
                        .nullable(),
                    })
                    .max(50, textTooLong(50))
                    .matches(
                      lettersOrNumbersWithExtraChars,
                      lettersOrNumbersWithExtraCharsMessage
                    ),
                  doctorName: yup
                    .string()
                    .when(['hasNoDoctorName', 'wasHospitalized'], {
                      is: (hasNoDoctorName: any, wasHospitalized: boolean) =>
                        hasNoDoctorName === true || wasHospitalized !== true,
                      then: yup.string().notRequired().nullable(),
                      otherwise: yup
                        .string()
                        .required(requiredMessage)
                        .nullable(),
                    })
                    .max(50, textTooLong(50))
                    .matches(
                      lettersOrNumbersNoSpecialChars,
                      lettersOrNumbersNoSpecialCharsMessage
                    ),
                  hospitalization: yup.string().notRequired().nullable(),
                  hospitalizationEndDate: yup
                    .string()
                    .when(['hasNoHospitalizationEndDate', 'wasHospitalized'], {
                      is: (
                        hasNoHospitalizationEndDate: any,
                        wasHospitalized: boolean
                      ) =>
                        hasNoHospitalizationEndDate === true ||
                        wasHospitalized !== true,
                      then: yup.string().notRequired().nullable(),
                      otherwise: yup
                        .string()
                        .required(requiredMessage)
                        .nullable(),
                    })
                    .test({
                      name: 'isGray',
                      message: endBeforeStart,
                      test: (value, context) => {
                        if (
                          value &&
                          isBefore(
                            new Date(value?.valueOf()),
                            new Date(
                              context.parent.hospitalizationStartDate.valueOf()
                            )
                          )
                        )
                          return false;
                        return true;
                      },
                    })
                    .nullable(),
                  hospitalizationStartDate: yup
                    .string()
                    .when('hasNoHospitalizationStartDate', {
                      is: (val: any) => val === true,
                      then: yup.string().notRequired().nullable(),
                      otherwise: yup
                        .string()
                        .required(requiredMessage)
                        .nullable(),
                    }),
                  medicalInstitutionName: yup
                    .string()
                    .max(50, textTooLong(50))
                    .matches(
                      lettersOrNumbersNoSpecialChars,
                      lettersOrNumbersNoSpecialCharsMessage
                    )
                    .required(requiredMessage)
                    .nullable(),
                })
              ),
            })
            .notRequired(),
          injuryKindSection: yup
            .object({
              investigationHasBeen: yup
                .boolean()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj.caseType[
                        index
                      ];
                    if (
                      currentCaseType.type ===
                        CASE_TYPE_RELATION_ID.INJURY_ID ||
                      currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                    ) {
                      if (value === null || value === undefined) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              investigatorOptionId: yup
                .string()
                .when(['investigationHasBeen', 'hasNoInvestigator'], {
                  is: (
                    investigationHasBeen: boolean,
                    hasNoInvestigator: boolean
                  ) => {
                    return (
                      investigationHasBeen === true &&
                      hasNoInvestigator === false
                    );
                  },
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .nullable(),
              whenInjuryHappanedOptionId: yup
                .string()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj.caseType[
                        index
                      ];
                    if (
                      currentCaseType.type ===
                        CASE_TYPE_RELATION_ID.INJURY_ID ||
                      currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                    ) {
                      if (!value) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              militaryBaseLocation: yup
                .string()
                .when('whenInjuryHappanedOptionId', {
                  is: (val: any) => val == ON_THE_WAY_TO_BASE_OR_FROM_BASE,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(50, textTooLong(50)),
              residentiaAddress: yup
                .string()
                .when('whenInjuryHappanedOptionId', {
                  is: (val: any) => val == ON_THE_WAY_TO_BASE_OR_FROM_BASE,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(50, textTooLong(50)),
              injuryLocation: yup
                .string()
                .when('whenInjuryHappanedOptionId', {
                  is: (val: any) => val == ON_THE_WAY_TO_BASE_OR_FROM_BASE,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(50, textTooLong(50)),
              isStopedOnIntermediateDest: yup
                .boolean()
                .when('whenInjuryHappanedOptionId', {
                  is: (val: any) => val == ON_THE_WAY_TO_BASE_OR_FROM_BASE,
                  then: yup.boolean().required(requiredMessage).nullable(),
                  otherwise: yup.boolean().notRequired().nullable(),
                })
                .nullable(),
              stopPurpose: yup
                .string()
                .when('isStopedOnIntermediateDest', {
                  is: (val: any) => val === true,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(50, textTooLong(50)),
              hasNoInvestigator: yup.boolean().notRequired().nullable(),
              hasNoInvestigationReportFile: yup
                .boolean()
                .notRequired()
                .nullable(),
              isInjuryDescribedInFile: yup
                .boolean()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj.caseType[
                        index
                      ];
                    if (
                      currentCaseType.type ===
                        CASE_TYPE_RELATION_ID.INJURY_ID ||
                      currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                    ) {
                      if (value === null || value === undefined) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              date: yup
                .string()
                .test({
                  name: bornDateAndInjury,
                  message: bornDateAndInjury,
                  test: (value) => {
                    const userBirthDate =
                      store.getState().caseDetails.generalObj.birthDate;
                    if (
                      value &&
                      userBirthDate &&
                      isBefore(
                        new Date(value?.valueOf()),
                        new Date(userBirthDate.valueOf())
                      )
                    ) {
                      return false;
                    }
                    return true;
                  },
                })
                .test({
                  name: injuryDate,
                  message: injuryDate,
                  test: (value) => {
                    if (
                      value &&
                      isBefore(new Date(), new Date(value?.valueOf()))
                    ) {
                      return false;
                    }
                    return true;
                  },
                })
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj;
                    if (currentCaseType.IsCausedBySpecificEvent[index]?.type) {
                      if (value === null || value === undefined) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              description: yup
                .string()
                .when('isInjuryDescribedInFile', {
                  is: (val: any) => val === false,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .max(500, textTooLong(500))
                .matches(
                  lettersOrNumbersWithExtraChars,
                  lettersOrNumbersWithExtraCharsMessage
                )
                .nullable(),
              reportAttachedSelectedOptionId: yup
                .string()
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    const index = extractIndexFromPath(context.path);
                    const currentCaseType =
                      store.getState().caseDetails.caseDetailsObj.caseType[
                        index
                      ];
                    if (
                      currentCaseType.type ===
                        CASE_TYPE_RELATION_ID.INJURY_ID ||
                      currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                    ) {
                      if (!value) return false;
                    }
                    return true;
                  },
                })
                .nullable(),
              isInjuryReportFileClassified: yup
                .boolean()
                .notRequired()
                .nullable(),
            })
            .notRequired(),
          isTrafficAccident: yup
            .boolean()
            .test({
              name: 'isRequired',
              message: requiredMessage,
              test: (value, context) => {
                const index = extractIndexFromPath(context.path);
                const currentCaseType =
                  store.getState().caseDetails.caseDetailsObj.caseType[index];
                if (
                  currentCaseType.type === CASE_TYPE_RELATION_ID.INJURY_ID ||
                  currentCaseType.type === CASE_TYPE_RELATION_ID.ALL
                ) {
                  if (value === null || value === undefined) return false;
                }
                return true;
              },
            })
            .nullable(),
          militaryServiceSection: yup
            .object({
              serviceTypeId: yup.string().required(requiredMessage).nullable(),
              otherServiceType: yup
                .string()
                .max(50, textTooLong(50))
                .matches(
                  lettersOrNumbersWithExtraChars,
                  lettersOrNumbersWithExtraCharsMessage
                )
                .test({
                  name: 'isRequired',
                  message: requiredMessage,
                  test: (value, context) => {
                    if (context.parent.serviceTypeId == 12) {
                      if (!value) {
                        return false;
                      }
                    }
                    return true;
                  },
                })
                .nullable(),
              hasNoReleaseDate: yup.boolean().notRequired().nullable(),
              releaseDate: yup
                .string()
                .when(['serviceTypeId', 'hasNoReleaseDate'], {
                  is: (serviceTypeId: string, hasNoReleaseDate: boolean) =>
                    serviceTypeId !== undefined &&
                    serviceTypeId !== null &&
                    hasNoReleaseDate === false,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                }),
              role: yup
                .string()
                .max(50, textTooLong(50))
                .matches(
                  lettersOrNumbersWithExtraChars,
                  lettersOrNumbersWithExtraCharsMessage
                )
                .required(requiredMessage)
                .nullable(),
              hasNoMedicalProfile: yup.boolean().notRequired().nullable(),
              medicalProfile: yup
                .string()
                .max(2)
                .when('hasNoMedicalProfile', {
                  is: (val: boolean) => val === false,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .matches(onlyNumbers, onlyNumbersMessage),
              hasNoTroop: yup.boolean().notRequired().nullable(),
              troopId: yup.string().when('hasNoTroop', {
                is: (val: boolean) => val === false,
                then: yup.string().required(requiredMessage).nullable(),
                otherwise: yup.string().notRequired().nullable(),
              }),
            })
            .notRequired(),
          caseTypeSection: yup
            .object({
              isInjury: yup.boolean().notRequired(),
              isDisease: yup.boolean().notRequired(),
              isPostTrauma: yup.boolean().notRequired(),
              isMentalIllness: yup.boolean().notRequired(),
            })
            .test({
              name: 'isRequired',
              message: requiredMessage,
              test: (value) => {
                if (!value) return true;
                if (
                  value.isInjury ||
                  value.isDisease ||
                  value.isPostTrauma ||
                  value.isMentalIllness
                )
                  return true;
                return false;
              },
            })
            .notRequired()
            .nullable(),
          areThereOtherWitnesses: yup
            .boolean()
            .when('areThereWitnessesOptionId', {
              is: (areThereWitnessesOptionId: any) => {
                return areThereWitnessesOptionId === 1;
              },
              then: yup.boolean().required(requiredMessage).nullable(),
              otherwise: yup.boolean().notRequired().nullable(),
            }),
          otherWitnessesDetails: yup
            .string()
            .when(['areThereWitnessesOptionId', 'areThereOtherWitnesses'], {
              is: (
                areThereWitnessesOptionId: any,
                areThereOtherWitnesses: any
              ) => {
                return (
                  areThereWitnessesOptionId === UNKNOWN_WITNESSES_ID ||
                  areThereOtherWitnesses === true
                );
              },
              then: yup.string().required(requiredMessage).nullable(),
              otherwise: yup.string().notRequired().nullable(),
            })
            .max(500, textTooLong(500))
            .nullable(),
          commanderDetailsSection: yup
            .object({
              firstName: yup
                .string()
                .max(20, textTooLong(20))
                .required(requiredMessage)
                .nullable(),
              lastName: yup
                .string()
                .max(20, textTooLong(20))
                .required(requiredMessage)
                .nullable(),
              phonePrefixRelationId: yup
                .string()
                .required(requiredMessage)
                .nullable(),
              phoneNumber: yup.string().required(requiredMessage).nullable(),
              civilAddress: yup
                .string()
                .when('hasNoCivilAddress', {
                  is: (val: boolean) => val === false,
                  then: yup.string().required(requiredMessage).nullable(),
                  otherwise: yup.string().notRequired().nullable(),
                })
                .nullable(),
              hasNoCivilAddress: yup.boolean().notRequired().nullable(),
              hasNoCommanderDocument: yup.boolean().notRequired().nullable(),
              isAlsoWitness: yup.boolean().required(requiredMessage).nullable(),
            })
            .notRequired()
            .nullable(),
          trafficAccidentSection: yup
            .object({})
            .when('isTrafficAccident', {
              is: (val: any) => val === true,
              then: yup.object({
                whatHaveYouDoDuringAccidentOptionId: yup
                  .number()
                  .required(requiredMessage)
                  .nullable(),
                vehicleDetailsSection: yup
                  .object({})
                  .when('whatHaveYouDoDuringAccidentOptionId', {
                    is: (val: any) => val == WAS_DRIVER_DURING_ACCIDENT_ID,
                    then: yup.object({
                      ownershipVehicleId: yup
                        .string()
                        .required(requiredMessage)
                        .nullable(),
                      vehicleId: yup
                        .string()
                        .required(requiredMessage)
                        .max(20, textTooLong(20))
                        .matches(onlyNumbers, onlyNumbersMessage)
                        .nullable(),
                    }),
                    otherwise: yup.object({
                      ownershipVehicleId: yup.string().notRequired().nullable(),
                      vehicleId: yup
                        .string()
                        .notRequired()
                        .max(20, textTooLong(20))
                        .matches(onlyNumbers, onlyNumbersMessage)
                        .nullable(),
                    }),
                  }),
                anotherVehicleInvolvedAccident: yup
                  .boolean()
                  .required(requiredMessage)
                  .nullable(),
                isDrivingLicenseExist: yup
                  .boolean()
                  .when('whatHaveYouDoDuringAccidentOptionId', {
                    is: (val: any) => val == WAS_DRIVER_DURING_ACCIDENT_ID,
                    then: yup.boolean().required(requiredMessage).nullable(),
                    otherwise: yup.boolean().notRequired().nullable(),
                  }),
                isVehicleInsuranceExist: yup
                  .boolean()
                  .when('whatHaveYouDoDuringAccidentOptionId', {
                    is: (val: any) => val == WAS_DRIVER_DURING_ACCIDENT_ID,
                    then: yup.boolean().required(requiredMessage).nullable(),
                    otherwise: yup.boolean().notRequired().nullable(),
                  }),
                compensationClaimSentOptionId: yup
                  .string()
                  .required(requiredMessage)
                  .nullable(),
                insuranceCompany: yup
                  .string()
                  .when('compensationClaimSentOptionId', {
                    is: (val: any) =>
                      val !== null &&
                      val != TRAFFIC_ACCIDENT_NO_INSURANCE_SUE_ID,
                    then: yup.string().required(requiredMessage).nullable(),
                    otherwise: yup.string().notRequired().nullable(),
                  })
                  .max(50, textTooLong(50))
                  .matches(
                    lettersOrNumbersWithExtraChars,
                    lettersOrNumbersWithExtraCharsMessage
                  )
                  .notRequired()
                  .nullable(),
                isPoliceReportExist: yup
                  .boolean()
                  .required(requiredMessage)
                  .nullable(),
                isPoliceReportFileClassified: yup
                  .boolean()
                  .notRequired()
                  .nullable(),
              }),
              otherwise: yup.object({
                whatHaveYouDoDuringAccidentOptionId: yup
                  .number()
                  .notRequired()
                  .nullable(),
                vehicleDetailsSection: yup.object({
                  ownershipVehicleId: yup.string().notRequired().nullable(),
                  vehicleId: yup
                    .string()
                    .notRequired()
                    .max(20, textTooLong(20))
                    .matches(onlyNumbers, onlyNumbersMessage)
                    .nullable(),
                }),
                anotherVehicleInvolvedAccident: yup
                  .boolean()
                  .notRequired()
                  .nullable(),
                isDrivingLicenseExist: yup.boolean().notRequired().nullable(),
                isVehicleInsuranceExist: yup.boolean().notRequired().nullable(),
                compensationClaimSentOptionId: yup
                  .string()
                  .notRequired()
                  .nullable(),
                insuranceCompany: yup
                  .string()
                  .max(50, textTooLong(50))
                  .matches(
                    lettersOrNumbersWithExtraChars,
                    lettersOrNumbersWithExtraCharsMessage
                  )
                  .notRequired()
                  .nullable(),
                isPoliceReportExist: yup.boolean().notRequired().nullable(),
              }),
            })
            .nullable(),
          wasMedicalInstitutionTreatment: yup
            .boolean()
            .required(requiredMessage)
            .nullable(),
          witnesses: yup.array().when('areThereWitnessesOptionId', {
            is: (val: any) => val == KNOWN_WITNESSES_ID,
            then: yup.array().of(
              yup.object({
                firstName: yup
                  .string()
                  .max(20, textTooLong(20))
                  .matches(
                    lettersOrNumbersNoSpecialChars,
                    lettersOrNumbersNoSpecialCharsMessage
                  )
                  .required(requiredMessage)
                  .nullable(),
                lastName: yup
                  .string()
                  .max(30, textTooLong(30))
                  .matches(
                    lettersOrNumbersNoSpecialChars,
                    lettersOrNumbersNoSpecialCharsMessage
                  )
                  .required(requiredMessage)
                  .nullable(),
                typeId: yup.number().required(requiredMessage).nullable(),
                phonePrefixRelationId: yup
                  .string()
                  .required(requiredMessage)
                  .nullable(),
                phone: yup
                  .string()
                  .test({
                    name: 'witnessPhoneNumber',
                    message: phoneNumberMessage,
                    test: (value) => {
                      if (value) {
                        if (value.length === 7) {
                          return true;
                        }
                        return false;
                      }
                      return true;
                    },
                  })
                  .matches(onlyNumbers, onlyNumbersMessage)
                  .required(requiredMessage)
                  .nullable(),
                role: yup
                  .string()
                  .max(50, textTooLong(50))
                  .required(requiredMessage)
                  .nullable()
                  .matches(
                    lettersOrNumbersWithExtraChars,
                    lettersOrNumbersWithExtraCharsMessage
                  ),
                hasNoCivilAddress: yup.boolean().notRequired().nullable(),
                civilAddress: yup.string().when('hasNoCivilAddress', {
                  is: (val: any) => val === false,
                  then: yup
                    .string()
                    .max(100, textTooLong(100))
                    .matches(
                      lettersOrNumbersNoSpecialChars,
                      lettersOrNumbersNoSpecialCharsMessage
                    )
                    .required(requiredMessage),
                  otherwise: yup.string().notRequired().nullable(),
                }),
                hasNoWitnessDocument: yup.boolean().notRequired().nullable(),
              })
            ),
            otherwise: yup.array().of(yup.object({})),
          }),
          id: yup.number().required(),
        })
      )
      .notRequired(),
  });
